import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Header, Footer, CategoryIcon, MobileBottomNav } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { betsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Clock, Users, ArrowLeft, Share2, AlertCircle
} from 'lucide-react';

const BetDetails = () => {
  const { betId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bet, setBet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBet = async () => {
      try {
        const { data } = await betsApi.getById(betId);
        setBet(data);
      } catch (err) {
        toast.error('Bet not found');
        navigate('/browse');
      } finally {
        setLoading(false);
      }
    };
    fetchBet();
  }, [betId, navigate]);

  const handleMatch = async (option) => {
    try {
      await betsApi.match({ bet_id: betId, chosen_option: option });
      toast.success('Bet matched successfully!');
      // Refresh bet data
      const { data } = await betsApi.getById(betId);
      setBet(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to match bet');
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A]">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!bet) return null;

  const isCreator = bet.creator_id === user?.id;
  const isMatcher = bet.matcher_id === user?.id;
  const canMatch = !isCreator && bet.status === 'open' && !bet.matched;
  const isExpired = new Date(bet.deadline) < new Date();

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col pb-20 md:pb-0">
      <Header />
      
      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="card-dark" data-testid="bet-details">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#2D2D2D] rounded-lg flex items-center justify-center border border-[#D4AF37]/30">
                <CategoryIcon category={bet.category} className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <div>
                <div className="badge-gold text-xs">{bet.category}</div>
                <div className={`text-xs font-bold uppercase mt-1 ${
                  bet.status === 'open' ? 'text-[#D4AF37]' :
                  bet.status === 'active' ? 'text-[#28A745]' :
                  bet.status === 'pending_approval' ? 'text-blue-400' :
                  bet.status === 'resolved' ? 'text-gray-400' :
                  'text-red-500'
                }`}>
                  {bet.status.replace('_', ' ')}
                </div>
              </div>
            </div>
            <Button onClick={handleShare} variant="outline" size="sm" className="border border-[#D4AF37]/30 text-white hover:bg-[#2D2D2D]">
              <Share2 className="h-4 w-4 mr-1" /> Share
            </Button>
          </div>

          {/* Title & Description */}
          <h1 className="text-2xl font-black uppercase mb-2 text-white" style={{ fontFamily: 'Outfit' }}>
            {bet.title}
          </h1>
          <p className="text-gray-400 mb-6">{bet.description}</p>

          {/* Stake Info */}
          <div className="bg-[#2D2D2D] rounded-lg p-4 border border-[#D4AF37]/30 mb-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-xs text-gray-400 uppercase">Stake</div>
                <div className="text-xl font-black text-[#D4AF37]">
                  {bet.stake_type === 'real' ? '$' : ''}{bet.stake_amount}{bet.stake_type === 'qbean' ? ' QBC' : ''}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400 uppercase">Total Pot</div>
                <div className="text-xl font-black text-[#28A745]">
                  {bet.stake_type === 'real' ? '$' : ''}{bet.stake_amount * 2}{bet.stake_type === 'qbean' ? ' QBC' : ''}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400 uppercase">Deadline</div>
                <div className="text-sm font-bold flex items-center justify-center gap-1 text-white">
                  <Clock className="h-4 w-4" />
                  {new Date(bet.deadline).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>

          {/* Rules */}
          {bet.rules && (
            <div className="bg-[#2D2D2D] rounded-lg p-4 border border-[#D4AF37]/30 mb-6">
              <h3 className="text-[#D4AF37] font-bold text-sm mb-2 uppercase">Bet Rules</h3>
              <p className="text-white text-sm break-words">{bet.rules}</p>
            </div>
          )}

          {/* Escrow Info */}
          <div className="bg-[#2D2D2D] rounded-lg p-3 mb-6 flex items-center justify-between text-sm">
            <div className="text-gray-400">Escrow Status:</div>
            <div className={`font-bold ${bet.matched ? 'text-[#28A745]' : 'text-[#D4AF37]'}`}>
              {bet.matched ? 'Both stakes in escrow' : 'Initiator stake locked'}
            </div>
          </div>

          {/* Match Button */}
          {canMatch && !isExpired && (
            <Button 
              onClick={() => handleMatch('b')}
              className="btn-green w-full mb-6"
              data-testid="match-bet-btn"
            >
              Accept Challenge & Match Bet
            </Button>
          )}

          {/* Status Messages */}
          {bet.status === 'pending_approval' && (
            <div className="bg-blue-500/10 border border-blue-500 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-blue-400" />
              <div>
                <div className="font-semibold text-blue-400">Pending Review</div>
                <p className="text-sm text-gray-400">This bet is being reviewed by our moderation team.</p>
              </div>
            </div>
          )}

          {isExpired && bet.status === 'open' && (
            <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <div>
                <div className="font-semibold text-red-500">Expired</div>
                <p className="text-sm text-gray-400">This bet has expired without being matched.</p>
              </div>
            </div>
          )}

          {bet.status === 'resolved' && (
            <div className="bg-[#28A745]/10 border border-[#28A745] rounded-lg p-4">
              <div className="font-semibold text-[#28A745] mb-1">Bet Resolved</div>
              <p className="text-sm text-gray-400">
                Winner: {bet.outcome === 'a' ? bet.option_a : bet.option_b}
              </p>
            </div>
          )}

          {/* Participants */}
          <div className="border-t border-[#3D3D3D] pt-6 mt-6">
            <h3 className="font-bold mb-3 flex items-center gap-2 text-white">
              <Users className="h-5 w-5 text-[#D4AF37]" /> Participants
            </h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#D4AF37] rounded-full flex items-center justify-center text-[#1A1A1A] font-bold text-sm">
                  {bet.creator_name?.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-sm text-white">{bet.creator_name}</div>
                  <div className="text-xs text-gray-400">Creator</div>
                </div>
              </div>
              
              {bet.matcher_name && (
                <>
                  <span className="text-lg font-bold text-[#D4AF37]">VS</span>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#28A745] rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {bet.matcher_name?.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-white">{bet.matcher_name}</div>
                      <div className="text-xs text-gray-400">Challenger</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/browse">
            <Button className="btn-secondary">Browse More Bets</Button>
          </Link>
        </div>
      </main>

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default BetDetails;
